import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Link2, Radio, Video } from 'lucide-react';
import type {
  CustomizedManualConfig,
  CustomizedManualItem,
  HydratedArticleItem,
  HydratedVideoItem,
} from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  groupContentsArticlePickerQueryOptions,
  groupContentsVideoPickerQueryOptions,
} from '@/lib/group-contents';
import { ArticlePickerDialog } from './article-picker-dialog';
import { VideoPickerDialog } from './video-picker-dialog';
import { ReorderableItemList } from './reorderable-item-list';

interface Props {
  config: CustomizedManualConfig;
  maxCount: number;
  onChange: (next: CustomizedManualConfig) => void;
}

/**
 * Mixed-type editor — each row picks its own itemType (article / video /
 * url / streaming). Top toolbar has 4 add buttons; clicking one either
 * opens the matching picker dialog (article/video) or appends a blank
 * inline-editable row (url/streaming).
 *
 * Article/video rows are read-only — admin re-picks via remove + re-add.
 * url + streaming rows have inline form fields so admin can edit in
 * place. Inline shape is the minimal payload (title+url for url, url
 * only for streaming); for fancier link cards use type=url groups.
 */
export function ConfigEditorCustomizedManual({ config, maxCount, onChange }: Props) {
  const [articlePickerOpen, setArticlePickerOpen] = useState(false);
  const [videoPickerOpen, setVideoPickerOpen] = useState(false);
  const [articleCache, setArticleCache] = useState<Record<string, HydratedArticleItem>>({});
  const [videoCache, setVideoCache] = useState<Record<string, HydratedVideoItem>>({});

  // Same recent-30 hydration trick as article-manual / video-manual.
  // Lazy-fired ONLY when the customized group actually contains items of
  // that type — saves a query for groups that mix differently.
  const hasArticles = config.items.some((it) => it.itemType === 'article');
  const hasVideos = config.items.some((it) => it.itemType === 'video');

  const articleLookup = useQuery({
    ...groupContentsArticlePickerQueryOptions(),
    staleTime: 60_000,
    enabled: hasArticles || articlePickerOpen,
  });
  useEffect(() => {
    if (!articleLookup.data) return;
    setArticleCache((prev) => {
      const next = { ...prev };
      for (const a of articleLookup.data) if (!next[a.id]) next[a.id] = a;
      return next;
    });
  }, [articleLookup.data]);

  const videoLookup = useQuery({
    ...groupContentsVideoPickerQueryOptions('', null),
    staleTime: 60_000,
    enabled: hasVideos || videoPickerOpen,
  });
  useEffect(() => {
    if (!videoLookup.data) return;
    setVideoCache((prev) => {
      const next = { ...prev };
      for (const v of videoLookup.data) if (!next[v.id]) next[v.id] = v;
      return next;
    });
  }, [videoLookup.data]);

  const updateItem = (idx: number, patch: Partial<CustomizedManualItem>) => {
    const items = config.items.map((it, i) =>
      i === idx ? ({ ...it, ...patch } as CustomizedManualItem) : it,
    );
    onChange({ ...config, items });
  };

  const append = (item: CustomizedManualItem) => {
    if (config.items.length >= maxCount) return;
    onChange({ ...config, items: [...config.items, item] });
  };
  const addArticle = (article: HydratedArticleItem) => {
    if (config.items.some((it) => it.itemType === 'article' && it.postId === article.id)) return;
    setArticleCache((prev) => ({ ...prev, [article.id]: article }));
    append({ itemType: 'article', postId: article.id });
  };
  const addVideo = (video: HydratedVideoItem) => {
    if (config.items.some((it) => it.itemType === 'video' && it.videoId === video.id)) return;
    setVideoCache((prev) => ({ ...prev, [video.id]: video }));
    append({ itemType: 'video', videoId: video.id });
  };
  const addUrl = () => append({ itemType: 'url', title: '', url: '' });
  const addStreaming = () => append({ itemType: 'streaming', url: '' });

  const remaining = maxCount - config.items.length;
  const atCap = remaining <= 0;

  const addToolbar = (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={atCap}
        onClick={() => setArticlePickerOpen(true)}
      >
        <FileText className="mr-1 h-3.5 w-3.5" /> บทความ
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={atCap}
        onClick={() => setVideoPickerOpen(true)}
      >
        <Video className="mr-1 h-3.5 w-3.5" /> วิดีโอ
      </Button>
      <Button type="button" variant="outline" size="sm" disabled={atCap} onClick={addUrl}>
        <Link2 className="mr-1 h-3.5 w-3.5" /> URL
      </Button>
      <Button type="button" variant="outline" size="sm" disabled={atCap} onClick={addStreaming}>
        <Radio className="mr-1 h-3.5 w-3.5" /> Stream
      </Button>
    </div>
  );

  const alreadyPickedArticleIds = config.items
    .filter(
      (it): it is Extract<CustomizedManualItem, { itemType: 'article' }> =>
        it.itemType === 'article',
    )
    .map((it) => it.postId);
  const alreadyPickedVideoIds = config.items
    .filter(
      (it): it is Extract<CustomizedManualItem, { itemType: 'video' }> => it.itemType === 'video',
    )
    .map((it) => it.videoId);

  return (
    <>
      <ReorderableItemList
        items={config.items}
        maxCount={maxCount}
        addSlot={addToolbar}
        emptyText='ยังไม่มีรายการ — กดปุ่ม "บทความ / วิดีโอ / URL / Stream" เพื่อเพิ่ม (ผสมข้ามชนิดได้)'
        onChange={(items) => onChange({ ...config, items })}
        getKey={(it, idx) => `${it.itemType}-${idx}`}
        renderItem={(it, idx) => (
          <CustomizedRow
            item={it}
            idx={idx}
            articleCache={articleCache}
            videoCache={videoCache}
            onPatch={(patch) => updateItem(idx, patch)}
          />
        )}
      />

      <ArticlePickerDialog
        open={articlePickerOpen}
        onOpenChange={setArticlePickerOpen}
        alreadyPicked={alreadyPickedArticleIds}
        onPick={addArticle}
      />
      <VideoPickerDialog
        open={videoPickerOpen}
        onOpenChange={setVideoPickerOpen}
        alreadyPicked={alreadyPickedVideoIds}
        onPick={addVideo}
      />
    </>
  );
}

interface CustomizedRowProps {
  item: CustomizedManualItem;
  idx: number;
  articleCache: Record<string, HydratedArticleItem>;
  videoCache: Record<string, HydratedVideoItem>;
  onPatch: (patch: Partial<CustomizedManualItem>) => void;
}

/** Per-row inline editor — dispatches on itemType. The ReorderableItemList
 *  shell wraps each row with the index number + up/down/remove buttons;
 *  we render only the type chip + content here. */
function CustomizedRow({ item, idx, articleCache, videoCache, onPatch }: CustomizedRowProps) {
  return (
    <>
      <TypeChip itemType={item.itemType} />
      {item.itemType === 'article' && (
        <ArticleRowContent postId={item.postId} cache={articleCache} />
      )}
      {item.itemType === 'video' && <VideoRowContent videoId={item.videoId} cache={videoCache} />}
      {item.itemType === 'url' && (
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Input
            value={item.title}
            onChange={(e) => onPatch({ title: e.target.value })}
            placeholder="หัวข้อ"
            className="h-7 px-2 text-sm"
            aria-label={`หัวข้อรายการ #${idx + 1}`}
          />
          <Input
            value={item.url}
            onChange={(e) => onPatch({ url: e.target.value })}
            placeholder="https://..."
            className="h-7 px-2 font-mono text-xs"
            spellCheck={false}
            autoComplete="off"
            aria-label={`URL รายการ #${idx + 1}`}
          />
        </div>
      )}
      {item.itemType === 'streaming' && (
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Label
            className="text-[10px] uppercase tracking-wide text-slate-400"
            htmlFor={`stream-url-${idx}`}
          >
            Stream URL
          </Label>
          <Input
            id={`stream-url-${idx}`}
            value={item.url}
            onChange={(e) => onPatch({ url: e.target.value })}
            placeholder="https://... (m3u8 / mpd / youtube live URL)"
            className="h-7 px-2 font-mono text-xs"
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      )}
    </>
  );
}

function TypeChip({ itemType }: { itemType: CustomizedManualItem['itemType'] }) {
  const style: Record<CustomizedManualItem['itemType'], { label: string; cls: string }> = {
    article: {
      label: 'บทความ',
      cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    },
    video: {
      label: 'วิดีโอ',
      cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
    },
    url: {
      label: 'URL',
      cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
    },
    streaming: {
      label: 'Stream',
      cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
    },
  };
  const s = style[itemType];
  return (
    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

function ArticleRowContent({
  postId,
  cache,
}: {
  postId: string;
  cache: Record<string, HydratedArticleItem>;
}) {
  const article = cache[postId];
  return (
    <>
      {article?.coverImageUrl ? (
        <img
          src={article.coverImageUrl}
          alt=""
          className="h-10 w-16 shrink-0 rounded object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
      ) : (
        <div className="h-10 w-16 shrink-0 rounded bg-slate-100 dark:bg-slate-800" />
      )}
      <div className="min-w-0 flex-1">
        <div className="line-clamp-1 text-sm font-medium text-slate-900 dark:text-slate-50">
          {article?.title ?? <code className="text-xs">{postId}</code>}
        </div>
        {article && (
          <div className="text-[11px] text-slate-400">{article.categoryName ?? 'ไม่ระบุหมวด'}</div>
        )}
      </div>
    </>
  );
}

function VideoRowContent({
  videoId,
  cache,
}: {
  videoId: string;
  cache: Record<string, HydratedVideoItem>;
}) {
  const video = cache[videoId];
  return (
    <>
      {video ? (
        <img
          src={video.thumbnailUrl}
          alt=""
          className="h-10 w-16 shrink-0 rounded object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
      ) : (
        <div className="h-10 w-16 shrink-0 rounded bg-slate-100 dark:bg-slate-800" />
      )}
      <div className="min-w-0 flex-1">
        <div className="line-clamp-1 text-sm font-medium text-slate-900 dark:text-slate-50">
          {video?.title ?? <code className="text-xs">{videoId}</code>}
        </div>
        {video && <div className="text-[11px] text-slate-400">{video.channelTitle ?? '—'}</div>}
      </div>
    </>
  );
}
